import { useState, useEffect } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Row, Select, LoadingSkeleton } from "@narmi/design_system";
import { Dialog } from "cerulean";
import { useGetTemplateVersions } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";

type CopyToNewTemplateDialogProps = {
  isOpen: boolean;
  templateUuid: API.BulkTemplateId | null;
  onClose: () => void;
  onCopy: (versionId: API.BulkTemplateVersionId) => void;
};

const CopyToNewTemplateDialog = ({
  isOpen,
  templateUuid,
  onClose,
  onCopy,
}: CopyToNewTemplateDialogProps) => {
  const { l10n } = useLocalization();
  const { getTemplateVersions, loading } = useGetTemplateVersions();
  const [versions, setVersions] = useState<API.BulkTemplateVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<
    API.BulkTemplateVersionId | "" | null
  >(null);

  useEffect(() => {
    if (isOpen && templateUuid) {
      getTemplateVersions(templateUuid, {
        onData: (data) => {
          setVersions(data);
          const latest = data.reduce<API.BulkTemplateVersion | null>(
            (max, v) =>
              !max || v.version_number > max.version_number ? v : max,
            null
          );
          setSelectedVersionId(latest?.id ?? null);
        },
      });
    }
  }, [isOpen, templateUuid, getTemplateVersions]);

  useEffect(() => {
    if (!isOpen) {
      setVersions([]);
      setSelectedVersionId(null);
    }
  }, [isOpen]);

  return (
    <Dialog
      width="min(595px, 90vw)"
      isOpen={isOpen}
      onUserDismiss={onClose}
      title={l10n.getString("copy-to-new-template-dialog-title")}
      footer={
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button kind="negative" onClick={onClose}>
              {l10n.getString("button-cancel")}
            </Button>
          </Row.Item>
          <Row.Item shrink>
            <Button
              disabled={!selectedVersionId}
              onClick={() => {
                if (selectedVersionId) {
                  onCopy(selectedVersionId);
                }
              }}
            >
              {l10n.getString("bulk-template-action-copy-to-new-template")}
            </Button>
          </Row.Item>
        </Row>
      }
    >
      <p className="margin--top--m margin--bottom--m">
        {l10n.getString("copy-to-new-template-dialog-description")}
      </p>
      <LoadingSkeleton isLoading={loading}>
        <Select
          label={l10n.getString("copy-to-new-template-dialog-select-label")}
          value={selectedVersionId ?? ""}
          onChange={(value: API.BulkTemplateVersionId | "") =>
            setSelectedVersionId(value)
          }
        >
          {versions.map((version) => (
            <Select.Item key={version.id} value={version.id}>
              {l10n.getString("copy-to-new-template-version-option", {
                versionNumber: version.version_number,
              })}
            </Select.Item>
          ))}
        </Select>
      </LoadingSkeleton>
    </Dialog>
  );
};

export default CopyToNewTemplateDialog;
